import { ItemInfo } from "@/components/commons/ItemInfo";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { validator } from "@/utils/validator";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

// Schema for inline memo validation (max 1000 chars)
const memoSchema = validator.object().shape({
  internalMemo: validator.string().trim().emptyToNull().max(1000).label("社内メモ").notRequired(),
});

type TMemoForm = { internalMemo: string };

type TCategoryInfoProps = {
  categoryName: string;
  status: string;
  publishedAt: string;
  notes: string;
  onMemoBlur?: (_value: string) => void;
  isSavingMemo?: boolean;
};

export const CategoryInfo: FC<TCategoryInfoProps> = ({ categoryName, status, publishedAt, notes, onMemoBlur, isSavingMemo }) => {
  const methods = useForm<TMemoForm>({
    defaultValues: { internalMemo: notes ?? "" },
    resolver: yupResolver(memoSchema) as never,
    mode: "onBlur",
  });

  const { reset } = methods;

  // Sync form value when notes changes from parent (e.g., after save memo)
  useEffect(() => {
    reset({ internalMemo: notes ?? "" });
  }, [notes, reset]);

  const handleBlur = async () => {
    const isValid = await methods.trigger("internalMemo");
    if (!isValid) return;
    const value = methods.getValues("internalMemo");
    if ((value ?? "").trim() !== (notes ?? "").trim()) {
      onMemoBlur?.(value);
    }
  };

  return (
    <Stack gap={3}>
      <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
        分野情報
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <ItemInfo label="分野名" value={categoryName} />
        </Grid>
        <Grid item xs={3}>
          <ItemInfo label="ステータス" value={status} />
        </Grid>
        <Grid item xs={3}>
          <ItemInfo label="有効化日" value={publishedAt} />
        </Grid>
      </Grid>

      {/* 社内メモ */}
      <FormProvider {...methods}>
        <FormField control={methods.control} name="internalMemo" label="社内メモ">
          <Input fullWidth placeholder="社内メモを入力してください" onBlur={handleBlur} disabled={isSavingMemo} />
        </FormField>
      </FormProvider>
    </Stack>
  );
};
